import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ReceiptIcon from "@material-ui/icons/Receipt";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PaymentDetails from "superAdminViews/finance/pay/PaymentDetails";
import TransactionDetails from "../transactions/TransactionDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "../superFinance.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The payment and count queries take the same filter set, so they are
 * one document with two root fields rather than two round trips kept in
 * sync by hand — the previous version fired them separately and the
 * count could sit a request behind the rows.
 * ------------------------------------------------------------------ */

const PAYMENT_FILTERS = `
  _id: $_id
  transactionId: $transactionId
  status: $status
  type: $type
  for: $for
  amountFrom: $amountFrom
  amount: $amount
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  shopName: $shopName
  shopPhoneNumber: $shopPhoneNumber
`;

const PAYMENT_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $transactionId: String
  $status: PaymentStatus
  $type: PaymentType
  $for: PaymentFor
  $amountFrom: Float
  $amount: Float
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
  $amountSort: Int
  $driverName: String
  $driverPhoneNumber: String
  $passengerName: String
  $passengerPhoneNumber: String
  $shopName: String
  $shopPhoneNumber: String
`;

const GET_PAYMENTS = gql`
  query getPaymentsByAdmin(${PAYMENT_VARIABLES}) {
    getPaymentsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${PAYMENT_FILTERS} }
      sort: { createdAt: $createdAtSort, amount: $amountSort }
    ) {
      _id
      for
      status
      type
      amount
      description
      createdAt
      updatedAt
      user {
        _id
        fullName
        phoneNumber
      }
      driver {
        _id
        fullName
        phoneNumber
      }
      shop {
        _id
        name
        phoneNumbers
      }
      trip {
        _id
      }
      order {
        _id
      }
      transactionId {
        _id
        transactionId
        status
        paidAt
        type
        amount
        reversed
        reversalId
        refundId
        paymentIntent
        createdAt
        updatedAt
        payments {
          _id
          for
          status
          description
          createdAt
          updatedAt
          amount
          type
          trip {
            _id
          }
          order {
            _id
          }
          shop {
            _id
            name
          }
          driver {
            _id
            fullName
          }
        }
        user {
          _id
          fullName
        }
        driver {
          _id
          fullName
        }
        shop {
          _id
          name
        }
      }
    }
    getPaymentsByAdminCount(filters: { ${PAYMENT_FILTERS} })
  }
`;

// Status totals across every payment, not just the page on screen. Cheap
// because each alias is a count, and they are what the metric strip
// claims to be showing.
const GET_STATUS_COUNTS = gql`
  query getPaymentStatusCounts {
    paid: getPaymentsByAdminCount(filters: { status: PAID })
    unpaid: getPaymentsByAdminCount(filters: { status: UNPAID })
    failed: getPaymentsByAdminCount(filters: { status: FAILED })
    failedDueToRejection: getPaymentsByAdminCount(filters: { status: FAILED_DUE_TO_REJECTION })
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "FAILED", label: "Failed" },
  { value: "FAILED_DUE_TO_REJECTION", label: "Failed (rejection)" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const TYPE_OPTIONS = [
  { value: "PAY_TO_SPARK", label: "Pay to Spark" },
  { value: "PAY_TO_SHOP", label: "Pay to shop" },
  { value: "PAY_TO_DRIVER", label: "Pay to driver" },
  { value: "REJECTION_PENALTY", label: "Rejection penalty" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const FOR_OPTIONS = [
  { value: "DELIVERY", label: "Delivery" },
  { value: "RIDE", label: "Ride" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "PURCHASE", label: "Purchase" },
];
const FOR_LABELS = FOR_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

/**
 * The old screen had six separate text inputs — driver name, driver
 * phone, passenger name, passenger phone, shop name, shop phone — plus
 * two ID boxes, and the backend ANDs them, so filling in more than one
 * almost always returned nothing. One box with a "search in" selector
 * says the same thing without the trap, and picks the name or phone
 * field by looking at what was typed.
 */
const SEARCH_TARGETS = [
  { value: "transactionId", label: "Transaction ref" },
  { value: "_id", label: "Payment ID" },
  { value: "shop", label: "Shop" },
  { value: "driver", label: "Driver" },
  { value: "passenger", label: "Customer" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const SEARCH_PLACEHOLDERS = {
  transactionId: "Search by transaction reference...",
  _id: "Search by payment ID (24 characters)...",
  shop: "Search by shop name or phone number...",
  driver: "Search by driver name or phone number...",
  passenger: "Search by customer name or phone number...",
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amountHigh", label: "Amount high-low" },
  { value: "amountLow", label: "Amount low-high" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const isObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value);

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

// Whichever of the three is set. A payment only ever has one.
const partyOf = (payment) => {
  if (payment.shop) return { role: "Shop", name: payment.shop.name };
  if (payment.driver) return { role: "Driver", name: payment.driver.fullName };
  if (payment.user) return { role: "Customer", name: payment.user.fullName };
  return null;
};

/**
 * Payments - every payment on the platform.
 *
 * Also serves /superAdmin/failedPayments, where `failed` pins the status
 * filter to FAILED and the copy changes to match.
 */
const Payments = (props) => {
  const failedOnly = Boolean(props.failed);

  const [searchTarget, setSearchTarget] = useState("transactionId");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(failedOnly ? "FAILED" : "");
  const [typeFilter, setTypeFilter] = useState("");
  const [forFilter, setForFilter] = useState("");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [paymentDetail, setPaymentDetail] = useState(null);
  const [transactionDetail, setTransactionDetail] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: -1,
    status: failedOnly ? "FAILED" : undefined,
  });

  // A malformed ObjectId is a cast error on the server, which fails the
  // whole query — so the ID search only reaches the wire once it could
  // possibly match something.
  const trimmedTerm = searchTerm.trim();
  const searchIsUsable =
    !trimmedTerm || searchTarget !== "_id" || isObjectId(trimmedTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (searchTarget === "transactionId") search.transactionId = term;
        else if (searchTarget === "_id") {
          if (isObjectId(term)) search._id = term;
        } else if (looksLikePhone(term)) {
          search[`${searchTarget}PhoneNumber`] = term;
        } else {
          search[`${searchTarget}Name`] = term;
        }
      }

      setVariables((v) => ({
        ...v,
        skip: 0,
        _id: undefined,
        transactionId: undefined,
        driverName: undefined,
        driverPhoneNumber: undefined,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        shopName: undefined,
        shopPhoneNumber: undefined,
        ...search,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        for: forFilter || undefined,
        amountFrom: amountFrom ? parseFloat(amountFrom) : undefined,
        amount: amountTo ? parseFloat(amountTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        amountSort: sortOption === "amountHigh" ? -1 : sortOption === "amountLow" ? 1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    statusFilter,
    typeFilter,
    forFilter,
    amountFrom,
    amountTo,
    dateFrom,
    dateTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_PAYMENTS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: statusCountsData } = useQuery(GET_STATUS_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_PAYMENTS, {
    fetchPolicy: "network-only",
  });

  const payments = (data && data.getPaymentsByAdmin) || [];
  const totalCount = (data && data.getPaymentsByAdminCount) || 0;
  const pageAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const counts = statusCountsData || {};
  const failedCount = (counts.failed || 0) + (counts.failedDueToRejection || 0);

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({
      id: "search",
      label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}`,
    });
  }
  if (statusFilter && !failedOnly) {
    activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  }
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (forFilter) activeFilterChips.push({ id: "for", label: `For: ${FOR_LABELS[forFilter]}` });
  if (amountFrom || amountTo) {
    activeFilterChips.push({
      id: "amount",
      label: `Amount: $${amountFrom || "0"} to $${amountTo || "-"}`,
    });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "status") setStatusFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "for") setForFilter("");
    if (id === "amount") {
      setAmountFrom("");
      setAmountTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    if (!failedOnly) setStatusFilter("");
    setTypeFilter("");
    setForFilter("");
    setAmountFrom("");
    setAmountTo("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      [
        "Transaction ref",
        "Payment ID",
        "Counterparty",
        "Role",
        "Type",
        "For",
        "Status",
        "Amount",
        "Created",
      ],
    ];
    exportData.getPaymentsByAdmin.forEach((p) => {
      const party = partyOf(p);
      rows.push([
        (p.transactionId && p.transactionId.transactionId) || "-",
        p._id,
        (party && party.name) || "-",
        (party && party.role) || "-",
        TYPE_LABELS[p.type] || p.type || "-",
        FOR_LABELS[p.for] || p.for || "-",
        STATUS_LABELS[p.status] || p.status,
        Number(p.amount || 0).toFixed(2),
        moment(p.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, failedOnly ? "FailedPayments.xlsx" : "Payments.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  const handleCopy = (value) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedId(value);
      window.setTimeout(() => setCopiedId(""), 1500);
    });
  };

  /* ---------------- render ---------------- */

  const title = failedOnly ? "Failed payments" : "Payments";
  const description = failedOnly
    ? "Payments that did not go through, so they can be chased or resolved by hand."
    : "Every payment on the platform, and which transaction it settled under.";

  if (loading && !data) {
    return (
      <div className="page superFinancePage superPaymentsPage">
        <main className="main">
          <PageHeader title={title} description={description} />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superFinancePage superPaymentsPage">
      <main className="main">
        <PageHeader
          title={title}
          count={totalCount}
          description={description}
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} payment${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard
            icon={<AccountBalanceWalletIcon />}
            title="Matching payments"
            value={totalCount}
            hint={activeFilterChips.length > 0 ? "With current filters" : "All payments"}
          />
          <MetricCard
            icon={<CheckCircleOutlineIcon />}
            title="Paid"
            value={counts.paid || 0}
            tone="success"
          />
          <MetricCard
            icon={<HourglassEmptyIcon />}
            title="Unpaid"
            value={counts.unpaid || 0}
            tone="warning"
          />
          <MetricCard
            icon={<ErrorOutlineIcon />}
            title="Failed"
            value={failedCount}
            tone={failedCount > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={SEARCH_PLACEHOLDERS[searchTarget]}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            {!failedOnly && (
              <FilterDropdown
                label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
                options={[{ value: "", label: "All statuses" }, ...STATUS_OPTIONS]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            )}
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={forFilter ? `For: ${FOR_LABELS[forFilter]}` : "For"}
              options={[{ value: "", label: "Anything" }, ...FOR_OPTIONS]}
              value={forFilter}
              onChange={(e) => setForFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Amount"
              fromValue={amountFrom}
              toValue={amountTo}
              onFromChange={(e) => setAmountFrom(e.target.value)}
              onToChange={(e) => setAmountTo(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {!searchIsUsable && (
          <div className="dsFormMessage warning" role="status">
            A payment ID is 24 characters — still waiting for a complete one before searching.
          </div>
        )}

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {payments.length > 0 && (
              <span className="superFinancePill">This page: ${pageAmount.toFixed(2)}</span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh payments"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superFinanceRefetchSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Transaction</div>
              <div className="tableHeaderCell">Counterparty</div>
              <div className="tableHeaderCell">Type / for</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Amount</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {payments.length === 0 ? (
              <EmptyState
                icon={<AccountBalanceWalletIcon />}
                title={activeFilterChips.length === 0 ? "No payments yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Payments appear here as orders and trips are paid for."
                    : "No payments match these filters. Try widening or clearing them."
                }
              />
            ) : (
              payments.map((payment) => {
                const ref = payment.transactionId && payment.transactionId.transactionId;
                const party = partyOf(payment);
                return (
                  <div className="row" key={payment._id}>
                    <div className="superFinanceRefCell" data-label="Transaction">
                      {ref ? (
                        <React.Fragment>
                          <span className="superFinanceRef">{ref}</span>
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => handleCopy(ref)}
                            title="Copy reference"
                          >
                            {copiedId === ref ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <FileCopyIcon style={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </React.Fragment>
                      ) : (
                        <span className="superFinanceRefMuted">No transaction</span>
                      )}
                    </div>

                    <div className="superFinanceParty" data-label="Counterparty">
                      {party ? (
                        <React.Fragment>
                          <span className="superFinancePartyName">{party.name || "Unnamed"}</span>
                          <span className="superFinancePartyRole">{party.role}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superFinanceRefMuted">-</span>
                      )}
                    </div>

                    <div className="superFinanceTypeCell" data-label="Type / for">
                      <span>{TYPE_LABELS[payment.type] || payment.type || "-"}</span>
                      {payment.for && (
                        <span className="superFinanceSubtext">
                          {FOR_LABELS[payment.for] || payment.for}
                        </span>
                      )}
                    </div>

                    <span data-label="Status">
                      <StatusBadge
                        status={STATUS_TONES[payment.status] || "neutral"}
                        label={STATUS_LABELS[payment.status] || payment.status}
                      />
                    </span>

                    <span className="superFinanceAmount" data-label="Amount">
                      ${Math.abs(payment.amount || 0).toFixed(2)}
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(payment.createdAt)}</strong>
                      <span>{formatTime(payment.createdAt)}</span>
                    </span>

                    <div className="actions">
                      {payment.transactionId && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={() => setTransactionDetail(payment.transactionId)}
                          title="View transaction"
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setPaymentDetail(payment)}
                        title="View payment"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(paymentDetail)}
        onClose={() => setPaymentDetail(null)}
        title="Payment details"
      >
        {paymentDetail && (
          <PaymentDetails
            close={() => setPaymentDetail(null)}
            refetch={() => refetch()}
            payment={paymentDetail}
            hasResolver={true}
          />
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(transactionDetail)}
        onClose={() => setTransactionDetail(null)}
        title="Transaction details"
      >
        {transactionDetail && (
          <TransactionDetails
            transaction={transactionDetail}
            close={() => setTransactionDetail(null)}
            refetch={() => refetch()}
            hasRefunder={false}
          />
        )}
      </DialogShell>
    </div>
  );
};

export default Payments;
